import { createContext, useContext } from "react";

import { Models } from "@/api/types";

export interface ProfileData {
  firstName?: string;
  lastName?: string;
  email?: string;
  dateOfBirth?: string;
  gender?: string;
  signUpSource?: string;
  referrerName?: string;
}

export interface FlowState {
  isNewUser: boolean;
}

export interface AuthState {
  phoneNumber?: string;
  verificationCode?: string;
  isAuthenticated: boolean;
  existingUser?: Models<"UserModel1">;
}

export interface PlatformSelectionState {
  platformId?: string;
  locationId?: string;
  canReselectPlatform: boolean;
}

export interface ReferralState {
  referralCode?: string;
  statelessReferralCode?: string;
  checkInCode?: string;
}

export interface FeatureState {
  hasScannedQRCode: boolean;
}

export interface OnboardingContextValue {
  // Grouped State
  flow: FlowState;
  auth: AuthState;
  platformSelection: PlatformSelectionState;
  referral: ReferralState;
  profile: ProfileData;
  features: FeatureState;

  // Flow Actions
  updateFlow: (updates: Partial<FlowState>) => void;

  // Auth Actions
  updateAuth: (updates: Partial<AuthState>) => void;

  // Platform Selection Actions
  updatePlatformSelection: (updates: Partial<PlatformSelectionState>) => void;
  setPlatformId: (platformId: string) => void;

  // Referral Actions
  updateReferral: (updates: Partial<ReferralState>) => void;

  // Profile Actions
  updateProfileState: (updates: Partial<ProfileData>) => void;
  clearProfile: () => void;

  // Feature Actions
  updateFeatures: (updates: Partial<FeatureState>) => void;

  // Utility Actions
  loadExistingUserData: () => Promise<Models<"UserModel1"> | null>;
  updateProfileAPI: (profileOverrides?: Partial<ProfileData>) => Promise<void>;
  onOnboardingCompleted: (profileOverrides?: Partial<ProfileData>) => Promise<void>;
  resetOnboardingFlow: () => void;
}

/**
 * Returns true when the user has already provided all profile fields
 * that the onboarding screens would ask for (name, email, gender).
 * DOB is intentionally excluded because it is optional ("Choose not to share").
 */
export function hasCompleteProfile(profile: ProfileData): boolean {
  return !!(profile.firstName && profile.lastName && profile.email && profile.gender);
}

export const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

export const useOnboardingContext = (): OnboardingContextValue => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboardingContext must be used within an OnboardingProvider");
  }
  return context;
};
